/**
 * BenchmarkCompareChart — overlays the user's portfolio TWR against an
 * external benchmark (or user-defined blend) over the chosen range.
 *
 * Portfolio side uses Time-Weighted Return so the comparison is
 * apples-to-apples: deposits and withdrawals don't distort the line.
 * Sits directly below the existing "Compare Performance" panel in the
 * Performance tab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Loader2, TrendingUp, TrendingDown, Info } from 'lucide-react';
import { toast } from 'sonner';
import { INITIAL_RC_DIM } from '../../utils/rechartsDefaults';
import CustomBenchmarkEditor from './CustomBenchmarkEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RANGES = [
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '6m', label: '6M' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'All' },
];

const authHeaders = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtPct = (v) => `${v >= 0 ? '+' : ''}${(v ?? 0).toFixed(2)}%`;

function mergeSeries(portfolioPts, benchPts) {
  const map = new Map();
  (portfolioPts || []).forEach((p) => map.set(p.date, { date: p.date, portfolio: p.cum_twr_pct }));
  (benchPts || []).forEach((p) => {
    const row = map.get(p.date) || { date: p.date };
    row.benchmark = p.indexed_pct;
    map.set(p.date, row);
  });
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

export default function BenchmarkCompareChart() {
  const [benchmarks, setBenchmarks] = useState([]);
  const [benchmarkId, setBenchmarkId] = useState('BLEND_60_40');
  const [range, setRange] = useState('1y');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showCustomEditor, setShowCustomEditor] = useState(false);

  // 1) Load available benchmarks once
  useEffect(() => {
    fetch(`${API_URL}/api/portfolio/performance/benchmarks`, { headers: authHeaders() })
      .then((r) => r.json()).then((d) => setBenchmarks(d.benchmarks || []))
      .catch(() => setBenchmarks([]));
  }, []);

  // 2) Fetch the comparison whenever inputs change (except while editor open)
  const fetchCompare = useCallback(async () => {
    if (benchmarkId === 'CUSTOM' && showCustomEditor) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch(
        `${API_URL}/api/portfolio/performance/benchmark?benchmark_id=${benchmarkId}&range=${range}`,
        { headers: authHeaders() },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Failed (${r.status})`);
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Failed to load benchmark comparison.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [benchmarkId, range, showCustomEditor]);

  useEffect(() => { fetchCompare(); }, [fetchCompare]);

  const chartData = data ? mergeSeries(data.portfolio?.data, data.benchmark?.data) : [];
  const portfolioFinal = data?.portfolio?.final_pct ?? 0;
  const benchFinal = data?.benchmark?.final_pct ?? 0;
  const diff = data?.difference_pct ?? 0;
  const isOutperforming = diff >= 0;

  return (
    <div className="portfolio-card p-4 sm:p-6 mt-6" data-testid="benchmark-compare-section">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base sm:text-lg font-serif font-bold text-slate-900 flex items-center gap-2">
            Compare with Benchmark
            <span className="text-xs font-normal text-slate-500 flex items-center gap-1">
              <Info className="w-3 h-3" /> Uses Time-Weighted Return so cash flows don&apos;t distort the comparison
            </span>
          </h3>
          <p className="body-text mt-1 text-sm">See how your portfolio compares to a market index or your own blend</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600" htmlFor="benchmark-select">Benchmark</label>
            <select
              id="benchmark-select"
              data-testid="benchmark-select"
              value={benchmarkId}
              onChange={(e) => {
                const v = e.target.value;
                setBenchmarkId(v);
                if (v === 'CUSTOM') setShowCustomEditor(true);
              }}
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 min-w-56"
            >
              {benchmarks.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-600">Range</span>
            <div className="flex gap-1" data-testid="benchmark-range">
              {RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setRange(r.value)}
                  className={`px-2.5 py-1.5 text-xs rounded-md border transition-colors ${
                    range === r.value
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`benchmark-range-${r.value}`}
                >{r.label}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {benchmarkId === 'CUSTOM' && (
        <CustomBenchmarkEditor
          open={showCustomEditor}
          onClose={() => setShowCustomEditor(false)}
          onSaved={() => { setShowCustomEditor(false); fetchCompare(); }}
          onCancel={() => setShowCustomEditor(false)}
        />
      )}

      {error && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 mb-3" data-testid="benchmark-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-400" data-testid="benchmark-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading comparison…
        </div>
      ) : chartData.length === 0 ? (
        <div className="text-center text-slate-500 text-sm py-12" data-testid="benchmark-empty">
          {benchmarkId === 'CUSTOM' && !data
            ? 'Set your custom weights to see the comparison.'
            : 'No data available for this range yet.'}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <SummaryCard label="Your portfolio (TWR)" value={portfolioFinal} testId="benchmark-portfolio-pct" />
            <SummaryCard label={data?.benchmark_label || 'Benchmark'} value={benchFinal} testId="benchmark-bench-pct" />
            <SummaryCard
              label="Difference"
              value={diff}
              testId="benchmark-diff-pct"
              icon={isOutperforming
                ? <TrendingUp className="w-4 h-4 text-emerald-600" />
                : <TrendingDown className="w-4 h-4 text-red-600" />}
            />
          </div>
          <div className="h-80" data-testid="benchmark-chart" data-sensitive="chart">
            <ResponsiveContainer width="100%" height="100%" debounce={1} initialDimension={INITIAL_RC_DIM}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
                <YAxis tickFormatter={(v) => `${v.toFixed(1)}%`} tick={{ fontSize: 11 }} width={70} domain={['dataMin - 2', 'dataMax + 2']} />
                <Tooltip
                  formatter={(v) => (v == null ? '—' : `${v.toFixed(2)}%`)}
                  labelStyle={{ color: '#475569', fontSize: 12 }}
                  contentStyle={{ borderRadius: 6, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone" dataKey="portfolio" name="Your portfolio (TWR)"
                  stroke="#0f172a" strokeWidth={2.2} dot={false} connectNulls
                />
                <Line
                  type="monotone" dataKey="benchmark" name={data?.benchmark_label || 'Benchmark'}
                  stroke="#10b981" strokeWidth={2} dot={false} strokeDasharray="4 3" connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, testId, icon }) {
  const positive = (value ?? 0) >= 0;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">{label}</div>
      <div className={`text-lg font-mono font-semibold flex items-center gap-1.5 ${positive ? 'text-slate-900' : 'text-red-600'}`}>
        {icon}
        {fmtPct(value)}
      </div>
    </div>
  );
}
